import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { SectionLabel } from "../components/Shared";
import { VideoDialog } from "../components/VideoDialog";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { ArrowRight, Calendar, MapPin, Play, Facebook, X } from "lucide-react";

const SCRUTIN = new Date("2026-09-27T08:30:00+02:00").getTime();

function Countdown() {
  const [left, setLeft] = useState(SCRUTIN - Date.now());
  useEffect(() => {
    const t = setInterval(() => setLeft(SCRUTIN - Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (left <= 0) return null;
  const units = [
    [Math.floor(left / 86400000), "jours"],
    [Math.floor((left % 86400000) / 3600000), "heures"],
    [Math.floor((left % 3600000) / 60000), "minutes"],
    [Math.floor((left % 60000) / 1000), "secondes"],
  ];
  return (
    <div data-testid="countdown-scrutin" className="mt-9 max-w-xl">
      <p className="text-[#F5C518] text-xs sm:text-sm font-extrabold uppercase tracking-[0.2em]">
        Temps restant avant l'ouverture des bureaux de vote
      </p>
      <div className="mt-3 grid grid-cols-4 gap-2 sm:gap-3">
        {units.map(([val, label]) => (
          <div key={label} className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 py-3 text-center">
            <span className="block font-display text-3xl sm:text-4xl font-bold text-white tabular-nums" data-testid={`countdown-${label}`}>
              {String(val).padStart(2, "0")}
            </span>
            <span className="block mt-1 text-[10px] sm:text-xs uppercase tracking-wider text-white/70 font-bold">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const [candidates, setCandidates] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [events, setEvents] = useState([]);
  const [video, setVideo] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [activeCandidate, setActiveCandidate] = useState(null);
  const [showSocial, setShowSocial] = useState(false);

  useEffect(() => {
    setShowSocial(false);
  }, [activeCandidate]);

  useEffect(() => {
    api.get("/candidates").then((r) => setCandidates(r.data)).catch(() => {});
    api.get("/proposals").then((r) => setProposals(r.data.slice(0, 4))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/videos").then((r) => setVideo(r.data.find((v) => v.featured) || r.data[0] || null)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative grain overflow-hidden bg-[#12495B]">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="fade-up">
            <SectionLabel variant="dark">Élections sénatoriales · Haut-Rhin · 27 septembre 2026</SectionLabel>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-white max-w-2xl uppercase">
              Pour une <span className="underline decoration-[#F5C518] decoration-4 underline-offset-[6px]">Alsace forte</span><br />
              dans une France qui respecte ses territoires :
            </h1>
            <p className="mt-4 font-display text-xl sm:text-2xl text-white/95 uppercase leading-snug">
              1. Priorité à la santé,<br />
              2. Lutter contre la bureaucratie,<br />
              3. Sortir du Grand Est
            </p>

            <div data-testid="rassemblement-banner" className="mt-7 bg-[#99142C] border-t-4 border-[#F5C518] rounded-2xl px-6 py-6 max-w-xl text-center shadow-[0_12px_35px_rgb(0,0,0,0.35)]">
              <p className="text-lg sm:text-xl font-extrabold text-white uppercase tracking-wide leading-snug">
                Liste de rassemblement d'élus indépendants, de la Majorité Alsacienne, du mouvement alsacien Unser Land et de la société civile
              </p>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/static/unser-land-blanc.png`}
                alt="Unser Land"
                data-testid="rassemblement-logo"
                className="h-9 w-auto mx-auto mt-3"
              />
            </div>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/programme"
                data-testid="hero-programme-btn"
                className="px-7 py-3.5 rounded-full bg-[#F5C518] text-[#12495B] font-extrabold hover:bg-[#E0B400] transition-colors duration-300 flex items-center gap-2 shadow-[0_8px_30px_rgb(245,197,24,0.4)]"
              >
                Découvrir le programme <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="px-7 py-3.5 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors duration-300"
              >
                Nous rencontrer
              </Link>
            </div>

            <Countdown />

            {/* Pastille / tampon de campagne */}
            <div className="mt-10 flex items-center gap-6">
              <div
                data-testid="badge-grand-est"
                className="w-32 h-32 rounded-full bg-transparent text-white flex items-center justify-center text-center rotate-[7deg] border-[3px] border-white shrink-0"
              >
                <span className="px-2 text-xs leading-tight uppercase font-extrabold">
                  Garantie<br /><span className="text-2xl">0%</span><br />Grand Est
                </span>
              </div>
            </div>
          </div>

          <div className="fade-up" style={{ animationDelay: "0.15s" }}>
            <div data-testid="hero-candidates-mosaic" className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {candidates.map((c, i) => (
                <div key={c.id} className="relative rounded-xl overflow-hidden bg-[#0E3A49] shadow-[0_10px_30px_rgb(0,0,0,0.3)] flex flex-col">
                  <div className="relative">
                    <img src={c.photo} alt={c.name} className="w-full aspect-[3/4] object-cover object-top" />
                    <span className="absolute top-2 left-2 w-6 h-6 rounded-full bg-[#F5C518] text-[#12495B] text-xs font-extrabold flex items-center justify-center">{i + 1}</span>
                  </div>
                  <div className="px-2.5 pt-2 pb-2.5 flex flex-col flex-1">
                    <p className="text-white text-[15px] font-extrabold leading-tight">{c.name}</p>
                    <p className="text-white/75 text-[10.5px] leading-snug mt-0.5">
                      {(c.functions || []).filter((f) => f.bold).map((f) => f.text).join(" · ") || (c.functions && c.functions[0] && c.functions[0].text)}
                    </p>
                    <button
                      onClick={() => setActiveCandidate(c)}
                      data-testid={`mosaic-more-${i}`}
                      className="mt-auto pt-1.5 self-start text-[#F5C518] text-[11px] font-bold flex items-center gap-1 hover:gap-2 transition-all duration-300"
                    >
                      Voir la suite <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-white/70 text-sm text-center">Notre liste de six candidats, au service des territoires du Haut-Rhin</p>
          </div>
        </div>
      </section>

      {/* CANDIDATS */}
      <section data-testid="candidates-section" className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="max-w-2xl">
          <SectionLabel>Les six candidats</SectionLabel>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-[#12495B] font-medium">
            Une équipe ancrée dans nos territoires
          </h2>
          <p className="mt-5 text-[#4B5563] text-lg leading-relaxed">
            Élus locaux, chefs d'entreprise, professionnels de santé et acteurs du monde
            rural : une liste à l'image de la diversité du Haut-Rhin.
          </p>
        </div>

        <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {candidates.map((c, i) => (
            <article
              key={c.id}
              data-testid={`candidate-card-${i}`}
              className="group bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-200/70 hover:-translate-y-1 transition-transform duration-300 flex flex-col"
            >
              <div className="overflow-hidden aspect-square bg-[#12495B]">
                <img src={c.photo} alt={c.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-3xl text-[#12495B]">{c.name}</h3>
                  {c.tag && (
                    <span className="text-[10px] uppercase tracking-wider font-extrabold bg-[#F5C518] text-[#12495B] rounded-full px-2.5 py-1">{c.tag}</span>
                  )}
                </div>
                <p className="text-xs tracking-[0.15em] uppercase text-[#12495B] font-bold mt-1 flex items-center gap-1">
                  <MapPin size={12} /> {c.city}
                </p>
                {c.functions && c.functions.length > 0 && (
                  <ul className="mt-4 space-y-1 text-sm">
                    {c.functions.filter((f) => f.bold).map((f, k) => (
                      <li key={k} className="font-bold text-[#12495B]">{f.text}</li>
                    ))}
                  </ul>
                )}
                <button
                  onClick={() => setActiveCandidate(c)}
                  data-testid={`candidate-readmore-${i}`}
                  className="mt-4 self-start text-[#12495B] font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all duration-300"
                >
                  Voir la suite <ArrowRight size={16} />
                </button>
              </div>
            </article>
          ))}
        </div>

        <Dialog open={!!activeCandidate} onOpenChange={(o) => !o && setActiveCandidate(null)}>
          <DialogContent data-testid="candidate-dialog" className="max-w-3xl p-0 overflow-hidden bg-white max-h-[90vh]">
            <button
              onClick={() => setActiveCandidate(null)}
              data-testid="candidate-dialog-close"
              aria-label="Fermer la fiche"
              className="absolute right-3 top-3 z-30 w-11 h-11 rounded-full bg-[#F5C518] text-[#12495B] flex items-center justify-center shadow-lg hover:scale-105 transition-transform duration-200"
            >
              <X size={24} strokeWidth={3} />
            </button>
            <div className="max-h-[90vh] overflow-y-auto">
            {activeCandidate && (
              <div className="grid sm:grid-cols-5">
                <div className="sm:col-span-2 bg-[#12495B]">
                  <img
                    src={activeCandidate.photo}
                    alt={activeCandidate.name}
                    className="w-full aspect-square object-cover object-top sm:sticky sm:top-0"
                  />
                </div>
                <div className="sm:col-span-3 p-8">
                  <DialogTitle className="font-display text-3xl text-[#12495B]">{activeCandidate.name}</DialogTitle>
                  {activeCandidate.tag && <p className="text-sm font-semibold text-[#12495B] mt-1">{activeCandidate.tag}</p>}
                  <p className="text-xs tracking-[0.15em] uppercase text-[#12495B]/70 font-bold mt-1 flex items-center gap-1">
                    <MapPin size={12} /> {activeCandidate.city}
                  </p>
                  {activeCandidate.functions && activeCandidate.functions.length > 0 && (
                    <ul className="mt-4 space-y-1 text-sm">
                      {activeCandidate.functions.map((f, k) => (
                        <li key={k} className={f.bold ? "font-bold text-[#12495B]" : "text-[#4B5563]"}>{f.text}</li>
                      ))}
                    </ul>
                  )}
                  {activeCandidate.quote && (
                    <p className="mt-4 border-l-[3px] border-[#F5C518] pl-3 italic text-[#12495B] text-sm leading-relaxed">{activeCandidate.quote}</p>
                  )}
                  {activeCandidate.social_videos && activeCandidate.social_videos.length > 0 && (
                    <div className="mt-5">
                      <button
                        onClick={() => setShowSocial(!showSocial)}
                        data-testid="profile-social-button"
                        className="w-full bg-[#12495B] text-[#F5C518] font-extrabold uppercase tracking-wide text-base rounded-xl px-6 py-4 flex items-center justify-center gap-3 hover:bg-[#0E3A49] transition-colors duration-300"
                      >
                        <Facebook size={20} /> Sur les réseaux sociaux
                      </button>
                      {showSocial && (
                        <div className="mt-4 overflow-x-auto social-scrollbar pb-2" data-testid="social-videos-list">
                          <div className="flex w-max gap-3">
                            {activeCandidate.social_videos.map((v, k) => (
                              <a
                                key={k}
                                href={v.url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`social-video-${k}`}
                                className="w-52 shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-[#12495B] to-[#0E3A49] border border-white/10 group"
                              >
                                <div className="h-24 flex items-center justify-center relative">
                                  <span className="absolute top-2 left-2 text-[9px] uppercase tracking-wider font-extrabold bg-[#99142C] text-white rounded-full px-2 py-0.5">Vidéo</span>
                                  <span className="w-11 h-11 rounded-full bg-[#F5C518] text-[#12495B] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                    <Play size={18} fill="currentColor" />
                                  </span>
                                </div>
                                <p className="px-3 pb-3 text-white text-[11px] font-semibold leading-snug line-clamp-3">{v.title}</p>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="mt-5 space-y-4 text-[#4B5563] text-sm leading-relaxed">
                    {activeCandidate.bio.split("\n\n").map((para, k) => (
                      <p key={k}>{para}</p>
                    ))}
                  </div>
                  <button
                    onClick={() => setActiveCandidate(null)}
                    data-testid="candidate-dialog-close-bottom"
                    className="mt-8 w-full rounded-full bg-[#12495B] text-white font-bold py-3.5 flex items-center justify-center gap-2 hover:bg-[#0E3A49] transition-colors duration-300"
                  >
                    <X size={18} strokeWidth={3} /> Fermer
                  </button>
                </div>
              </div>
            )}
            </div>
          </DialogContent>
        </Dialog>
      </section>

      {/* PROGRAMME PREVIEW */}
      <section className="bg-white border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-xl">
              <SectionLabel>Notre programme</SectionLabel>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-[#12495B] font-medium">
                Nos trois priorités
              </h2>
            </div>
            <Link to="/programme" data-testid="programme-see-all" className="inline-flex items-center gap-2 hover:gap-3 rounded-full bg-[#F5C518] text-[#12495B] font-bold px-7 py-3.5 shadow-md hover:shadow-lg transition-all duration-300">
              Voir nos 3 priorités <ArrowRight size={18} />
            </Link>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {proposals.map((p, i) => (
              <div key={p.id} data-testid={`proposal-preview-${i}`} className="p-8 rounded-2xl bg-[#FDFBF7] border border-slate-200/70 hover:border-[#12495B]/50 transition-colors duration-300">
                <h3 className="font-display text-2xl text-[#12495B]">{p.theme}</h3>
                <p className="text-[#4B5563] mt-3 leading-relaxed">{p.summary}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/programme"
              data-testid="programme-cta-massif"
              className="inline-flex items-center justify-center gap-3 rounded-full bg-[#12495B] text-white font-bold text-lg sm:text-xl px-10 sm:px-14 py-5 shadow-[0_12px_40px_rgb(18,73,91,0.35)] hover:bg-[#0E3A49] hover:scale-[1.03] transition-all duration-300 w-full sm:w-auto"
            >
              Lire le détail de nos 3 priorités <ArrowRight size={24} />
            </Link>
            <p className="mt-4 text-sm text-[#4B5563]">Textes complets, engagements et mesures concrètes</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <SectionLabel>Prochains rendez-vous</SectionLabel>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-[#12495B] font-medium">Venez à notre rencontre</h2>
        <p className="mt-4 text-[#4B5563] max-w-2xl">Toutes les informations au jour le jour — rencontres, réunions publiques et déplacements — sont publiées dans nos actualités.</p>
        <Link to="/actualites" className="mt-8 inline-flex text-[#12495B] font-semibold items-center gap-2 hover:gap-3 transition-all duration-300">
          Suivre nos actualités <ArrowRight size={18} />
        </Link>
      </section>

      {/* VIDEO EN VEDETTE */}
      {video && (
        <section data-testid="home-video-section" className="bg-white border-y border-slate-200/70">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-12 items-center">
            <button
              onClick={() => setVideoOpen(true)}
              data-testid="home-video-thumb"
              className="group relative block rounded-2xl overflow-hidden bg-[#12495B] shadow-[0_20px_60px_rgb(0,0,0,0.15)] max-w-sm mx-auto lg:mx-0 w-full aspect-[9/16]"
            >
              <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-20 h-20 rounded-full bg-[#F5C518] text-[#12495B] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Play size={34} fill="currentColor" className="ml-1" />
                </span>
              </span>
            </button>
            <div>
              <SectionLabel>En vidéo</SectionLabel>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-[#12495B]">{video.title}</h2>
              {video.subtitle && <p className="mt-4 text-[#4B5563] text-lg">{video.subtitle}</p>}
              <p className="mt-4 text-[#4B5563] leading-relaxed">
                Découvrez nos prises de position en vidéo. Cliquez pour visionner.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <button onClick={() => setVideoOpen(true)} data-testid="home-video-watch" className="px-7 py-3.5 rounded-full bg-[#F5C518] text-[#12495B] font-extrabold hover:bg-[#E0B400] transition-colors duration-300 flex items-center gap-2">
                  <Play size={18} fill="currentColor" /> Voir la vidéo
                </button>
                <Link to="/videos" className="px-7 py-3.5 rounded-full border border-[#12495B]/20 text-[#12495B] font-semibold hover:bg-[#12495B]/5 transition-colors duration-300">
                  Toutes les vidéos
                </Link>
              </div>
            </div>
          </div>
          <VideoDialog video={video} open={videoOpen} onOpenChange={setVideoOpen} />
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="relative grain overflow-hidden rounded-3xl bg-[#12495B] px-8 py-16 sm:px-16 text-center">
          <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tight">Rejoignez notre mobilisation</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto text-lg">
            Un message, une question, une envie de vous engager ? Écrivez-nous.
          </p>
          <Link to="/contact" data-testid="cta-contact-btn" className="mt-8 inline-flex px-8 py-3.5 rounded-full bg-[#F5C518] text-[#12495B] font-extrabold hover:bg-[#E0B400] transition-colors duration-300">
            Nous contacter
          </Link>
        </div>
      </section>
    </div>
  );
}
